const TZ     = 'Asia/Kolkata';
const LOCALE  = 'en-IN';

// Returns date/time parts for the given date (defaults to now) in IST.
// Uses Intl so it works correctly regardless of the browser's local timezone.
function istParts(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const p = Object.fromEntries(fmt.formatToParts(date).map(({ type, value }) => [type, value]));
  // Some engines return '24' for midnight in 24h mode — normalise to '00'
  const hour = p.hour === '24' ? '00' : p.hour;
  return {
    year:          p.year,
    month:         p.month,
    day:           p.day,
    hour,
    minute:        p.minute,
    dateStr:       `${p.year}-${p.month}-${p.day}`,
    dateTimeLocal: `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`,
  };
}

// YYYY-MM-DD in IST for a date n whole days before now
function istDateNDaysAgo(n) {
  return istParts(new Date(Date.now() - n * 86400000)).dateStr;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

export function formatISTDate(date) {
  return new Date(date).toLocaleDateString(LOCALE, { timeZone: TZ });
}

export function formatISTTime(date) {
  return new Date(date).toLocaleTimeString(LOCALE, {
    timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export function formatISTDateTime(date) {
  return new Date(date).toLocaleString(LOCALE, {
    timeZone: TZ,
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// Full precision — includes seconds — for tables that need exact receipt times.
export function formatISTDateTimeSeconds(date) {
  return new Date(date).toLocaleString(LOCALE, {
    timeZone: TZ,
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

// Convert a datetime-local value ("YYYY-MM-DDTHH:MM", interpreted as IST)
// to a full ISO string that backends can parse correctly.
export function istLocalToISO(dtLocal) {
  if (!dtLocal) return '';
  return `${dtLocal}:00.000+05:30`;
}

// Build date range presets whose from/to values are datetime-local strings in IST.
export function getISTPresets() {
  const now = istParts();

  const [y, m] = [parseInt(now.year, 10), parseInt(now.month, 10)];
  const thisMonthStart = `${now.year}-${now.month}-01`;
  const prevMonth      = m === 1 ? 12 : m - 1;
  const prevYear       = m === 1 ? y - 1 : y;
  const prevMonthStr   = String(prevMonth).padStart(2, '0');
  const lastMonthStart = `${prevYear}-${prevMonthStr}-01`;
  // day 0 of the current month = last day of previous month (pure numeric, no TZ needed)
  const lastDayOfPrevMonth = new Date(y, m - 1, 0).getDate();
  const lastMonthEnd   = `${prevYear}-${prevMonthStr}-${String(lastDayOfPrevMonth).padStart(2, '0')}`;

  const yesterday = istDateNDaysAgo(1);

  return [
    { label: 'Today',        from: `${now.dateStr}T00:00`,        to: now.dateTimeLocal },
    { label: 'Yesterday',    from: `${yesterday}T00:00`,           to: `${yesterday}T23:59` },
    { label: 'Last 7 days',  from: `${istDateNDaysAgo(6)}T00:00`,  to: now.dateTimeLocal },
    { label: 'Last 30 days', from: `${istDateNDaysAgo(29)}T00:00`, to: now.dateTimeLocal },
    { label: 'This Month',   from: `${thisMonthStart}T00:00`,      to: now.dateTimeLocal },
    { label: 'Last Month',   from: `${lastMonthStart}T00:00`,      to: `${lastMonthEnd}T23:59` },
    { label: 'All Time',     from: '',                              to: '' },
  ];
}

// ─── Whole-day ranges ─────────────────────────────────────────────────────────

// 'YYYY-MM-DD' shifted by n days (calendar arithmetic, no timezone involved).
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

// Date ranges as whole Indian days ('YYYY-MM-DD', both ends included) — for
// reports that take a from/to date. Weeks start on Monday.
export function getISTDayRanges(now = new Date()) {
  const today = istParts(now).dateStr;
  const y = Number(today.slice(0, 4));
  const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7; // Monday = 0
  const thisWeekStart = addDays(today, -weekday);
  const thisMonthStart = `${today.slice(0, 7)}-01`;
  const lastMonthEnd = addDays(thisMonthStart, -1);

  return [
    { key: 'today',     label: 'Today',      from: today,                      to: today },
    { key: 'yesterday', label: 'Yesterday',  from: addDays(today, -1),         to: addDays(today, -1) },
    { key: 'thisWeek',  label: 'This week',  from: thisWeekStart,              to: today },
    { key: 'lastWeek',  label: 'Last week',  from: addDays(thisWeekStart, -7), to: addDays(thisWeekStart, -1) },
    { key: 'thisMonth', label: 'This month', from: thisMonthStart,             to: today },
    { key: 'lastMonth', label: 'Last month', from: `${lastMonthEnd.slice(0, 7)}-01`, to: lastMonthEnd },
    { key: 'last30',    label: 'Last 30 days', from: addDays(today, -29),      to: today },
    { key: 'thisYear',  label: 'This year',  from: `${y}-01-01`,               to: today },
    { key: 'lastYear',  label: 'Last year',  from: `${y - 1}-01-01`,           to: `${y - 1}-12-31` },
  ];
}
